import { useState, ReactElement } from 'react';

import { 
  Box, 
  Button, 
  Container, 
  Divider, 
  Typography, 
  Grid, 
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { useAnalytics, useScreenSize, useOperatorWithLoading } from "globals/hooks";
import { AppInfoHeader } from '../AppInfoSections';
import { PassengerAppSubscriptionDialog, PassengerAppInstalled } from './components';
import { MoovsMarketAppData } from 'pages/moovs-market/components/MoovsMarketAppList/hooks/useMoovsMarketApps';
import { aboutAppToTrack } from 'pages/moovs-market/helpers/enumToTrack';

import { 
  granite, 
  moovsBlue, 
  moovsBlueLightTransparent, 
  moovsBlueLight, 
  white, 
  black, 
  moovsBlueSelected 
} from 'design-system/colors';
import { 
  AppMobileCheckIcon,
  MobileBookingPaymentIcon,
  MultiPinTripIcon,
  PhoneAppCheckIcon,
  PlaystoreApplestoreIcon,
  HelpIcon,
} from 'design-system/icons';
import CheckFilledIcon from "design-system/icons/info/CheckFilledIcon";
import PassengerAppPreview from 'design-system/images/PassengerAppPreview.png';

import { PlanVariant } from 'types';

// Feature data
const APP_FEATURES = [
  {
    id: 'brandApp',
    title: 'Your brand, your app',
    Icon: <PhoneAppCheckIcon />,
    description: 'Give your customers a premium experience with your own branded app in the app store.',
    mobileDescription: 'Premium branded app experience in the app store'
  },
  {
    id: 'mobileBooking',
    title: 'Mobile booking & payment',
    Icon: <MobileBookingPaymentIcon />,
    description: 'Enable your customers to request quotes, confirm reservations, and complete payments.',
    mobileDescription: 'Request quotes, confirm reservations & complete payments'
  },
  {
    id: 'driverTracking',
    title: 'Real-time driver tracking',
    Icon: <MultiPinTripIcon />,
    description: 'Keep your customers informed at every turn with live driver tracking, instant status.',
    mobileDescription: 'Live driver tracking with instant status updates'
  },
  {
    id: 'availability',
    title: 'Available on iOS and Android',
    Icon: <PlaystoreApplestoreIcon />,
    description: 'Helps your customers stay connected to your service before, during, and after every ride.',
    mobileDescription: 'Stay connected across iOS and Android devices'
  }
];

// Pricing features
const PRICING_FEATURES = [
  { id: 'launch', text: 'Launch in less than 2 weeks' },
  { id: 'brand', text: 'Your brand, your app' },
  { id: 'availability', text: 'Available on any device' },
  { id: 'support', text: 'Dedicated account manager' }
];

type PassengerAppProps = {
  app: MoovsMarketAppData;
  onAppClose: () => void;
};

export function PassengerApp(props: PassengerAppProps) {
  const { app, onAppClose } = props;

  // Hooks
  const { track } = useAnalytics();
  const { isMobileView } = useScreenSize();
  const { operator, reloadOperator } = useOperatorWithLoading();
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  
  // Derived state
  const companyName = operator?.name || "Your Company";
  const isFreeUser = operator?.plan === PlanVariant.Free;
  
  // handlers
  const handleBackToMoovsMarket = () => {
    onAppClose();
  };

  const handleLearnMoreClick = () => {
    track(aboutAppToTrack[app.enum]);
    window.open(app.aboutPage, "_blank");
  };

  const handleSubscribeClick = () => {
    track('market_passengerAppSubscribeButtonClicked');
    setSubscriptionDialogOpen(true);
  };

  const handleScheduleCallClick = () => {
    track('market_passengerAppScheduleCallClicked');
    window.open("https://calendly.com/joan-moovsapp/passenger-app-demo", "_blank");
  };

  // Components
  const LearnMoreButton = () => (
    <Button 
      variant="outlined" 
      color="primary" 
      onClick={handleLearnMoreClick}
      fullWidth={isMobileView}
      sx={{ 
        borderRadius: '4px', 
        py: 1, 
        mt: 2, 
        mb: isMobileView ? 2 : 0, 
        borderWidth: '1px',
        '&:hover': {
          borderWidth: '1px' // Keep the same border width on hover
        }
      }}
    >
      Learn More
    </Button>
  );

  // Feature list item component for pricing section
  const FeatureListItem = ({ text }: { text: string }) => (
    <ListItem disableGutters dense>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <CheckFilledIcon 
          size="small"
          bgColor={moovsBlueLight} 
          checkmarkColor={white} 
        />
      </ListItemIcon>
      <ListItemText
        primary={<Typography variant="body2" color={white}>{text}</Typography>}
      />
    </ListItem>
  );

  // App feature item component with title and description
  const AppFeatureItem = ({ 
    title, 
    description, 
    mobileDescription,
    Icon 
  }: { 
    title: string; 
    description: string;
    mobileDescription: string;
    Icon: ReactElement;
  }) => (
    <ListItem disableGutters disablePadding sx={{ mb: isMobileView ? 1 : 1.5 }}>
      <ListItemIcon sx={{ 
        minWidth: 40, 
        pr: 2, 
        alignSelf: isMobileView ? 'center' : 'flex-start', 
        pt: isMobileView ? 0 : 1
      }}>
        {Icon}
      </ListItemIcon>
      <ListItemText 
        primary={
          <Typography variant="h4" color={black}>
            {title}
          </Typography>
        }
        secondary={
          <Typography variant="body2" color={black} sx={{ opacity: isMobileView ? 1 : 0.8 }}>
            {isMobileView ? mobileDescription : description}
          </Typography>
        }
      />
    </ListItem>
  );

  return (
    <Container maxWidth="md" sx={{ px: isMobileView ? 2 : 3, pb: 2 }}>
      {/* Moovs Market App Headers */}
      <AppInfoHeader
        onBack={handleBackToMoovsMarket}
        isActive={app.active}
      />

      <Divider sx={{ mb: isMobileView ? 1 : 2 }} />

      {app.active ? (
        <PassengerAppInstalled 
          app={app}
          isMobileView={isMobileView}
          companyName={companyName}
        />
      ) : (
        // Marketing and subscription screen for inactive app
        <Box mt={isMobileView ? 2 : 4}>
          <Grid container spacing={isMobileView ? 2 : 3}>
            {/* Section 1: Title and description */}
            <Grid item xs={12} md={6}>
              <Box mb={isMobileView ? 2 : 4} mt={isMobileView ? 0 : 3}>
                {/* Header: icon, label, company name */}
                <Box display="flex" mb={isMobileView ? 2 : 4.5}>
                  <Box 
                    width="70px" 
                    height="70px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="8px"
                    mr={2}
                  >
                    <img src={app.icon} alt="Passenger App" width="70" height="70" />
                  </Box>
                  <Box>
                    <Typography variant="overline" color={granite} fontWeight="500" letterSpacing="0.5px">
                      PASSENGER APP
                    </Typography>
                    <Typography variant="h2" color={black}>
                      {companyName}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h1" color={black} mb={isMobileView ? 1 : 2}>
                  Elevate Your Service with your own branded passenger app
                </Typography>
                <Typography variant="body1" color={black} lineHeight={1.6} mb={isMobileView ? 1 : 2}>
                  Elevate your service with a branded passenger app that sets you apart from competitors. Your logo, your colors, and an intuitive interface create a seamless extension of your brand that builds customer loyalty with every ride.
                </Typography>
                {/* Show Learn More button in desktop view in Section 1 */}
                {!isMobileView && <LearnMoreButton />}
              </Box>
            </Grid>

            {/* Section 2: App Preview Image */}
            <Grid item xs={12} md={6} mt={isMobileView ? 0 : 1}>
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="flex-start" 
                height="100%"
              >
                <img 
                  src={PassengerAppPreview}
                  alt="Passenger App Preview" 
                  style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '8px' }}
                />
              </Box>
            </Grid>

            {/* Section 3: Feature List */}
            <Grid item xs={12} md={6}>
              <Box 
                display="flex" 
                flexDirection="column" 
                justifyContent="center"
                height="100%"
              >
                <List>
                  {APP_FEATURES.map((feature) => (
                    <AppFeatureItem
                      key={feature.id}
                      title={feature.title}
                      description={feature.description}
                      mobileDescription={feature.mobileDescription}
                      Icon={feature.Icon}
                    />
                  ))}
                </List>
                
                {/* Show Learn More button in mobile view in Section 3 */}
                {isMobileView && <LearnMoreButton />}
              </Box>
            </Grid>

            {/* Section 4: Pricing */}
            <Grid item xs={12} md={6} mb={isMobileView ? 2 : 0} mt={0}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '8px',
                  bgcolor: moovsBlue,
                  color: 'white',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* Icon and price */}
                <Box display="flex" alignItems="center" mb={isMobileView ? 1 : 2} sx={{ alignSelf: 'flex-start' }}>
                  <Box pr={2} py={1} display="flex" alignItems="center" justifyContent="center">
                    <AppMobileCheckIcon />
                  </Box>
                  <Box display="flex" flexDirection="column" justifyContent="center">
                    <Typography variant="h3" color={"white"} pb={0.5}>
                      Just ${app.pricePerMonth}/month
                    </Typography>
                    <Typography variant="body2" color={"white"}>
                      Save $20,000+ compared to custom app development
                    </Typography>
                  </Box>
                </Box>

                {/* Features */}
                <List sx={{ width: '100%', mb: isMobileView ? 1 : 2 }}>
                  {PRICING_FEATURES.map((feature) => (
                    <FeatureListItem key={feature.id} text={feature.text} />
                  ))}
                </List>

                {/* Speak with a representative */}
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    bgcolor: moovsBlueLightTransparent,
                    borderRadius: '4px',
                    p: 1.5,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Box mr={1.25}>
                    <HelpIcon color={white} />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Typography variant="overline" color="white" pb={0.5}>
                      QUESTIONS?
                    </Typography>
                    <Button
                      sx={{ textTransform: 'none', p: 0, minHeight: 0 }}
                      onClick={handleScheduleCallClick}
                    >
                      <Typography variant="body2" color="white" fontWeight={500}>
                        Speak with a representative
                      </Typography>
                    </Button>
                  </Box>
                </Paper>

                {/* Subscribe button */}
                <Button
                  variant="contained"
                  color="inherit"
                  fullWidth
                  size="large"
                  onClick={handleSubscribeClick}
                  sx={{
                    py: 1.5,
                    borderRadius: '4px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '16px',
                    color: black,
                    bgcolor: white,
                  }}
                  endIcon={<ArrowForwardIcon sx={{ color: black }} />}
                >
                  Subscribe
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Subscription Dialog */}
      <PassengerAppSubscriptionDialog 
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        onSubscribeSuccess={reloadOperator}
        planName="Moovs Passenger App"
        bgColor={moovsBlueSelected}
        pricePerMonth={app.pricePerMonth}
        setupFee={app.setupFee}
        isFreeUser={isFreeUser}
      />
    </Container>
  );
} 